/**
 * Payment Matching Workflow
 *
 * This workflow automatically matches bank movements to invoices:
 * 1. Triggers when a new bank movement is created
 * 2. Fetches unpaid invoices
 * 3. Uses AI to match payment to invoice(s)
 * 4. Updates invoice status if confident match
 * 5. Notifies user of matches or requests manual review
 */

import type { Workflow } from './types';

// =============================================================================
// WORKFLOW DEFINITION
// =============================================================================

export const paymentMatchingWorkflow: Workflow = {
  id: 'payment-matching',
  name: 'Riconciliazione Pagamenti',
  description: 'Associa automaticamente i movimenti bancari alle fatture in sospeso',
  trigger: {
    type: 'event',
    config: {
      eventType: 'entity.created',
      entityType: 'movement',
      // Only for incoming payments
      filterField: 'type',
      filterValue: 'income',
    },
  },
  steps: [
    {
      id: 'fetch_invoices',
      name: 'Recupera Fatture Non Pagate',
      type: 'custom',
      config: {
        // Fetch unpaid invoices
        filterStatus: 'unpaid',
        // Look for matches within ±10% of payment amount
        amountTolerance: 0.10,
        // Consider invoices from last 12 months
        maxAgeMonths: 12,
      },
      next: ['match_payment'],
    },
    {
      id: 'match_payment',
      name: 'Associa Pagamento',
      type: 'ai_analyze',
      config: {
        prompt: `Match this bank movement to one or more invoices.

Payment details:
- Amount: {amount}
- Date: {date}
- Description: {description}
- Sender: {sender}

Available invoices:
{invoices}

Analyze and respond in JSON:
{
  "matches": [
    {
      "invoiceId": "...",
      "confidence": 0.0-1.0,
      "reason": "Why this matches",
      "amountMatch": true/false
    }
  ],
  "action": "auto_mark_paid|request_review|no_match"
}`,
        confidenceThreshold: 0.85,
      },
      next: ['update_invoice'],
    },
    {
      id: 'update_invoice',
      name: 'Aggiorna Fattura',
      type: 'custom',
      config: {
        // Only auto-update if confidence >= threshold
        autoUpdateIfConfident: true,
        confidenceThreshold: 0.85,
        markAsPaid: true,
        linkPayment: true,
      },
      condition: {
        field: 'confidence',
        operator: '>=',
        value: 0.85,
      },
      next: ['send_notification'],
    },
    {
      id: 'send_notification',
      name: 'Notifica Risultato',
      type: 'send_notification',
      config: {
        type: 'in_app',
        message: '{matchCount} fatture associate al pagamento di {amount}€.',
        requestReviewIfUncertain: true,
      },
    },
  ],
  enabled: true,
  version: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};
